import React from 'react';

interface DonutChartProps {
  data: { value: number; color: string; label: string;[key: string]: any }[];
  size?: number;
  strokeWidth?: number;
  onClick?: (item: { value: number; color: string; label: string;[key: string]: any }) => void;
}

const DonutChart: React.FC<DonutChartProps> = ({ data, size = 140, strokeWidth = 14, onClick }) => {
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);
  const total = data.reduce((acc, item) => acc + item.value, 0);
  let accumulatedCircumference = 0;
  let accumulatedPercent = 0;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  if (total === 0) {
    return (
      <div className="flex items-center justify-center" style={{ width: size, height: size }}>
        <div className="text-xs text-gray-400">No Data</div>
      </div>
    );
  }

  return (
    <div className="relative flex items-center justify-center group" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="transform -rotate-90">
        {data.map((item, index) => {
          const percentage = item.value / total;
          const strokeDasharray = `${percentage * circumference} ${circumference}`;
          const strokeDashoffset = -accumulatedCircumference;
          accumulatedCircumference += percentage * circumference;

          return (
            <g key={index}>
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="transparent"
                stroke={item.color}
                strokeWidth={strokeWidth}
                strokeDasharray={strokeDasharray}
                strokeDashoffset={strokeDashoffset}
                className="transition-all duration-300 ease-out"
                style={{
                  opacity: hoveredIndex === null ? 1 : hoveredIndex === index ? 1 : 0.3,
                  strokeWidth: hoveredIndex === index ? strokeWidth + 2 : strokeWidth
                }}
              />
            </g>
          );
        })}

        {/* Interaction Layer - Invisible Wedges */}
        {data.map((item, index) => {
          const percent = item.value / total;
          const startPercent = accumulatedPercent;
          accumulatedPercent += percent;

          // Handle 100% case
          if (percent > 0.9999) {
            return (
              <circle
                key={`wedge-${index}`}
                cx={size / 2}
                cy={size / 2}
                r={size / 2}
                fill="transparent"
                className="cursor-pointer"
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onClick) onClick(item);
                }}
              />
            );
          }

          const endPercent = startPercent + percent;

          const startX = size / 2 + (size / 2) * Math.cos(2 * Math.PI * startPercent);
          const startY = size / 2 + (size / 2) * Math.sin(2 * Math.PI * startPercent);
          const endX = size / 2 + (size / 2) * Math.cos(2 * Math.PI * endPercent);
          const endY = size / 2 + (size / 2) * Math.sin(2 * Math.PI * endPercent);

          const largeArcFlag = percent > 0.5 ? 1 : 0;

          const pathData = [
            `M ${size / 2} ${size / 2}`,
            `L ${startX} ${startY}`,
            `A ${size / 2} ${size / 2} 0 ${largeArcFlag} 1 ${endX} ${endY}`,
            `Z`
          ].join(' ');

          return (
            <path
              key={`wedge-${index}`}
              d={pathData}
              fill="transparent"
              className="cursor-pointer"
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              onClick={(e) => {
                e.stopPropagation();
                if (onClick) onClick(item);
              }}
            />
          );
        })}
      </svg>
      <div className="absolute flex flex-col items-center justify-center pointer-events-none">
        {hoveredIndex !== null ? (
          <>
            <span className="text-2xl font-bold transition-colors duration-300" style={{ color: data[hoveredIndex].color }}>{data[hoveredIndex].value}</span>
            <span className="text-[9px] uppercase tracking-wider transition-colors duration-300" style={{ color: data[hoveredIndex].color }}>{data[hoveredIndex].label}</span>
            <span className="text-[8px] transition-colors duration-300" style={{ color: data[hoveredIndex].color, opacity: 0.8 }}>
              {((data[hoveredIndex].value / total) * 100).toFixed(1)}%
            </span>
          </>
        ) : (
          <>
            <span className="text-2xl font-bold text-light-text-primary dark:text-white">{total}</span>
            <span className="text-[9px] text-light-text-muted dark:text-gray-400 uppercase tracking-wider">Total</span>
          </>
        )}
      </div>
    </div>
  );
};

export default DonutChart;